import CryptoJS from 'crypto-js'
import JSEncrypt from 'jsencrypt'

/**
 * 生成随机32位字符串
 * @returns {string} 随机生成的32位字符串
 */
function generateRandomString() {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const charactersLength = characters.length
  for (let i = 0; i < 32; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }
  return result
}

/**
 * 生成AES密钥
 * @returns {object} 包含原始字符串和密钥对象
 */
export function generateAESKey() {
  const randomStr = generateRandomString()
  return {
    raw: randomStr,
    key: CryptoJS.enc.Utf8.parse(randomStr)
  }
}

/**
 * 使用AES加密数据
 * @param {string|object} data - 要加密的数据
 * @param {WordArray} key - AES密钥(WordArray格式)
 * @returns {string} 加密后的Base64字符串
 */
export function encryptAES(data, key) {
  // 如果data是对象,先转换为JSON字符串
  const dataStr = typeof data === 'object' ? JSON.stringify(data) : String(data)

  // 使用AES加密(ECB模式,PKCS7填充)
  const encrypted = CryptoJS.AES.encrypt(dataStr, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })

  // 返回Base64编码的密文
  return encrypted.toString()
}

/**
 * 使用AES解密数据
 * @param {string} encryptedData - 加密的数据(Base64格式)
 * @param {string} key - AES密钥(原始字符串格式)
 * @returns {string} 解密后的字符串
 */
export function decryptAES(encryptedData, key) {
  // 将原始字符串密钥转换为WordArray
  const keyWordArray = CryptoJS.enc.Utf8.parse(key)

  // 解密
  const decrypted = CryptoJS.AES.decrypt(encryptedData, keyWordArray, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  })

  // 转换为UTF8字符串
  return decrypted.toString(CryptoJS.enc.Utf8)
}

/**
 * Base64编码
 * @param {WordArray} wordArray - WordArray对象
 * @returns {string} Base64编码后的字符串
 */
export function encryptBase64(wordArray) {
  return CryptoJS.enc.Base64.stringify(wordArray)
}

/**
 * 使用RSA公钥加密AES密钥
 * @param {WordArray} aesKeyWordArray - AES密钥(WordArray格式)
 * @param {string} publicKey - RSA公钥(PEM格式)
 * @returns {string} 加密后的Base64字符串
 */
export function encryptRSA(aesKeyWordArray, publicKey) {
  // 将AES密钥转为Base64
  const aesKeyBase64 = encryptBase64(aesKeyWordArray)

  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(publicKey)

  // 使用RSA加密Base64编码的AES密钥
  const encrypted = encrypt.encrypt(aesKeyBase64)

  if (!encrypted) {
    throw new Error('RSA加密失败')
  }

  return encrypted
}

/**
 * 使用RSA私钥解密
 * @param {string} encryptedData - 加密的数据
 * @param {string} privateKey - RSA私钥(PEM格式)
 * @returns {string} 解密后的字符串
 */
export function decryptRSA(encryptedData, privateKey) {
  const decrypt = new JSEncrypt()
  decrypt.setPrivateKey(privateKey)

  const decrypted = decrypt.decrypt(encryptedData)

  if (!decrypted) {
    throw new Error('RSA解密失败')
  }

  return decrypted
}

/**
 * 对请求体进行完整加密处理
 * @param {object} requestData - 要加密的请求数据
 * @param {string} publicKey - RSA公钥
 * @returns {object} 包含加密后的数据和加密后的AES密钥以及原始密钥
 */
export function encryptRequestBody(requestData, publicKey) {
  try {
    // 1. 生成随机AES密钥
    const aesKeyObj = generateAESKey()
    const aesKeyRaw = aesKeyObj.raw
    const aesKey = aesKeyObj.key

    // console.log('生成的AES密钥(原始32位字符串):', aesKeyRaw)
    // console.log('AES密钥(Base64编码):', encryptBase64(aesKey))

    // 2. 使用AES密钥加密请求体
    const encryptedBody = encryptAES(requestData, aesKey)

    // 3. 使用RSA公钥加密AES密钥(会先转为Base64再加密)
    const encryptedAESKey = encryptRSA(aesKey, publicKey)

    return {
      encryptedBody,
      encryptedAESKey,
      rawAESKey: aesKeyRaw // 返回原始密钥用于解密
    }
  } catch (error) {
    console.error('加密请求体失败:', error)
    throw error
  }
}

// 缓存用于解密的AES密钥
const decryptionKeys = new Map()

/**
 * 缓存解密所需的AES密钥
 * @param {string} requestId - 请求ID
 * @param {string} aesKey - 用于解密的AES密钥
 */
export function cacheDecryptionKey(requestId, aesKey) {
  decryptionKeys.set(requestId, aesKey)
}

/**
 * 获取用于解密的AES密钥
 * @param {string} requestId - 请求ID
 * @returns {string|null} AES密钥或null
 */
export function getDecryptionKey(requestId) {
  return decryptionKeys.get(requestId) || null
}

/**
 * 清除指定的解密密钥
 * @param {string} requestId - 请求ID
 */
export function clearDecryptionKey(requestId) {
  decryptionKeys.delete(requestId)
}

/**
 * 对加密的响应进行解密
 * @param {string} encryptedResponse - 加密的响应数据
 * @param {string} aesKey - AES密钥
 * @returns {object} 解密后的数据对象
 */
export function decryptResponseBody(encryptedResponse, aesKey) {
  try {
    const decrypted = decryptAES(encryptedResponse, aesKey)
    return JSON.parse(decrypted)
  } catch (error) {
    console.error('解密响应失败:', error)
    throw error
  }
}

export default {
  generateAESKey,
  encryptAES,
  decryptAES,
  encryptBase64,
  encryptRSA,
  decryptRSA,
  encryptRequestBody,
  decryptResponseBody
}
